import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { get, put, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/PageHeader';
import { RevisionRow, type Revision } from '@/components/domain/RevisionRow';
import { StoreRevisionRow } from '@/components/domain/StoreRevisionRow';
import { StoreRevisionDrawer } from '@/components/domain/StoreRevisionDrawer';
import { SubmissionWorkerDrawer, type SubmissionDetail } from '@/components/domain/SubmissionWorkerDrawer';
import { EditProductDialog } from '@/components/domain/EditProductDialog';
import { productOfTier } from '@/lib/prize-tiers';
import { matchesQuery, mergeRevisionQueue, type StoreRevision } from '@/lib/revision-queue';

// Revisions = prize submissions in changes_requested AND open team-store
// change requests, in one list. Prize rows open the worker drawer (tier cards,
// logo replace/text, regenerate & send proof); store rows open the request
// drawer.

const LIST_URL = '/api/revisions?limit=100';

type ListPayload = {
  revisions: Revision[];
  storeRevisions?: StoreRevision[];
  storeRevisionsError?: string | null;
};

export default function Revisions() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [openStore, setOpenStore] = useState<StoreRevision | null>(null);
  const [q, setQ] = useState('');
  const [editTier, setEditTier] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const { data, error, isLoading } = useSWR<ListPayload>(LIST_URL, get);
  const { data: detail, isLoading: detailLoading } = useSWR<SubmissionDetail>(
    openId ? `/api/submissions/${openId}` : null, get
  );

  const all = mergeRevisionQueue(data?.revisions || [], data?.storeRevisions || []);
  const items = all.filter((i) => matchesQuery(i, q));

  async function refreshAll(id: string) {
    await Promise.all([
      mutate(`/api/submissions/${id}`),
      mutate(LIST_URL),
    ]);
  }

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Revisions"
        subtitle={`Edits requested on sent proofs — prize fundraisers and team stores · ${items.length}${q ? ` of ${all.length}` : ''} shown`}
      />

      <div className="mb-3 flex max-w-[420px]">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by school, store, sport, notes…" className="flex-1" />
      </div>

      {data?.storeRevisionsError ? (
        <p className="mb-3 rounded-lg bg-danger/10 px-3 py-2 text-[12px] text-danger">
          Team-store requests failed to load — only prize revisions are shown. Refresh to retry.
        </p>
      ) : null}

      {error && data ? (
        <p className="mb-2 text-[11.5px] text-muted">Couldn't refresh — showing the last loaded list.</p>
      ) : null}

      <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error && !data ? (
          <div className="p-10 text-center text-[12px] text-danger">
            Revisions failed to load — refresh to retry.
          </div>
        ) : items.length ? (
          items.map((i) => (i.kind === 'prize'
            ? <RevisionRow key={i.key} r={i.prize} showKind onClick={() => setOpenId(i.prize.id)} />
            : <StoreRevisionRow key={i.key} r={i.store} onClick={() => setOpenStore(i.store)} />))
        ) : (
          <div className="p-10 text-center text-[12px] text-muted">
            No revisions {q ? `match “${q}”` : 'found'}.
          </div>
        )}
      </div>

      <SubmissionWorkerDrawer
        open={!!openId}
        onClose={() => setOpenId(null)}
        submission={detail}
        loading={detailLoading}
        onEditTier={(tier) => setEditTier(tier)}
      />

      <StoreRevisionDrawer
        item={openStore}
        onClose={() => setOpenStore(null)}
        onChanged={() => mutate(LIST_URL)}
      />

      <EditProductDialog
        open={editTier !== null}
        onOpenChange={(o) => { if (!o) setEditTier(null); }}
        tier={editTier ?? 1}
        currentProductId={
          editTier != null && detail
            ? productOfTier(detail as unknown as Record<string, unknown>, editTier)
            : undefined
        }
        busy={saving}
        onConfirm={async (newProductId) => {
          if (!openId || !editTier) return;
          setSaving(true);
          try {
            const body = editTier <= 2
              ? { [`tier${editTier}Product`]: newProductId }
              : { tier: editTier, product: newProductId };
            await put(`/api/submissions/${openId}/products`, body);
            toast.success(`Product ${editTier} changed`);
            setEditTier(null);
            await refreshAll(openId);
          } catch (e) {
            const body = (e as ApiError).body as { message?: string; error?: string } | null;
            toast.error(body?.message || body?.error || 'Save failed');
            await refreshAll(openId);
          } finally {
            setSaving(false);
          }
        }}
      />
    </div>
  );
}
